import { useEffect, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Checkbox,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { Category } from '@mui/icons-material';

function AbilityItem({ ability, isChecked, onChange, allowEdit, isDisabled }) {
  return (
      <TableRow
          sx={{
            height: 40,
            '&:hover': { backgroundColor: 'action.hover' },
          }}
      >
        <TableCell padding="checkbox" sx={{ width: 50 }}>
          <Checkbox
              checked={isChecked}
              onChange={(e) => onChange(e.target.checked)}
              disabled={!allowEdit || isDisabled}
              sx={{ cursor: !allowEdit || isDisabled ? 'not-allowed' : 'pointer' }}
          />
        </TableCell>
        <TableCell>
          <Typography
              variant="body2"
              fontWeight="medium"
              color={isDisabled ? 'text.disabled' : 'text.primary'}
          >
            {ability.titulo}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
              variant="body2"
              color={isDisabled ? 'text.disabled' : 'text.secondary'}
          >
            {ability.descricao}
          </Typography>
        </TableCell>
      </TableRow>
  );
}

export function AbilitiesCheckboxes({ abilities, pessoa, control, setValue, allowEdit }) {
  const [childChecked, setChildChecked] = useState({});
  const [disabledIds, setDisabledIds] = useState([]);

  // === Agrupa abilities por sufixo ===
  const safeLower = (str) => (str || '').toLowerCase();

  const getGroupFromNome = (nome) => {
    const match = (nome || '').match(/_(\w+)$/);
    let group = match ? match[1] : 'geral';
    group = safeLower(group);
    if (group === 'administracao') group = 'geral';
    return group;
  };

  const groupAbilitiesBySuffix = (abilities) => {
    return abilities.reduce((acc, ability) => {
      const group = getGroupFromNome(ability.nome);
      if (!acc[group]) acc[group] = [];
      acc[group].push(ability);
      return acc;
    }, {});
  };

  useEffect(() => {
    if (!abilities || abilities.length === 0) return;

    const grouped = groupAbilitiesBySuffix(abilities);
    const initialChecked = {};
    const initialDisabled = [];

    Object.entries(grouped).forEach(([group, list]) => {
      initialChecked[group] = list.reduce((acc, a) => {
        acc[a.id] = pessoa?.abilities?.includes(a.id) || false;
        return acc;
      }, {});
    });

    let newValue = [...(pessoa?.abilities || [])];

    // === Caso ADMIN geral ===
    const geralAdmin = abilities.find((a) => a.nome === 'admin');
    if (geralAdmin && pessoa?.abilities?.includes(geralAdmin.id)) {
      Object.entries(grouped).forEach(([g, list]) => {
        list.forEach((a) => {
          initialChecked[g][a.id] = true;
          if (a.id !== geralAdmin.id) initialDisabled.push(a.id);
        });
      });
      if (!newValue.includes(geralAdmin.id)) newValue.push(geralAdmin.id);
    }

    // === Caso GERENCIAR_X ===
    const gerenciarAbilities = abilities.filter((a) =>
        safeLower(a.titulo).startsWith('gerenciar')
    );

    gerenciarAbilities.forEach((a) => {
      if (pessoa?.abilities?.includes(a.id)) {
        const match = a.nome.match(/_(\w+)$/);
        const suffix = match ? safeLower(match[1]) : null;
        if (suffix && grouped[suffix]) {
          grouped[suffix].forEach((ab) => {
            initialChecked[suffix][ab.id] = true;
            if (ab.id !== a.id) initialDisabled.push(ab.id);
          });
          // Só envia o ID do gerenciar
          if (!newValue.includes(a.id)) newValue.push(a.id);
        }
      }
    });

    setChildChecked(initialChecked);
    setDisabledIds(initialDisabled);
    setValue('abilities', newValue, { shouldDirty: false });
  }, [abilities, pessoa]);


  return (
      <Controller
          name="abilities"
          control={control}
          defaultValue={[]}
          render={({ field: { onChange, value } }) => {
            const grouped = groupAbilitiesBySuffix(abilities);

            const updateAbilities = (newChecked, newDisabled, newValue) => {
              setChildChecked(newChecked);
              setDisabledIds(newDisabled);
              setValue('abilities', newValue, { shouldDirty: true });
              onChange(newValue);
            };

            return (
                <Grid container spacing={2} sx={{ py: 1 }}>
                  {Object.entries(grouped).map(([group, list]) => (
                      <Grid item xs={12} key={group}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Stack direction="row" alignItems="center">
                                <Category sx={{ mr: 1 }} color="info" />
                                {group.charAt(0).toUpperCase() + group.slice(1)}
                              </Stack>
                            </Typography>

                            <TableContainer>
                              <Table size="small">
                                <TableHead sx={{ backgroundColor: 'primary.main' }}>
                                  <TableRow>
                                    <TableCell sx={{ color: 'white', width: 50 }}>Ativo</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Habilidade</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Descrição</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {list.map((ability) => {
                                    const isAdmin = ability.nome === 'admin';
                                    const isGerenciar = safeLower(ability.titulo).startsWith('gerenciar');

                                    return (
                                        <AbilityItem
                                            key={ability.id}
                                            ability={ability}
                                            isChecked={childChecked[group]?.[ability.id] || false}
                                            isDisabled={disabledIds.includes(ability.id)}
                                            allowEdit={allowEdit}
                                            onChange={(checked) => {
                                              const newChecked = structuredClone(childChecked);
                                              const newDisabled = [...disabledIds];
                                              let newValue = [...value];

                                              // === ADMIN SELECIONADO ===
                                              if (isAdmin) {
                                                if (checked) {
                                                  Object.entries(grouped).forEach(([g, abilitiesList]) => {
                                                    abilitiesList.forEach((a) => {
                                                      newChecked[g][a.id] = true;
                                                      if (a.id !== ability.id) newDisabled.push(a.id);
                                                    });
                                                  });
                                                  newValue = [ability.id];
                                                } else {
                                                  Object.entries(grouped).forEach(([g, abilitiesList]) => {
                                                    abilitiesList.forEach((a) => {
                                                      newChecked[g][a.id] = false;
                                                    });
                                                  });
                                                  newDisabled.length = 0;
                                                  newValue = [];
                                                }

                                                updateAbilities(newChecked, newDisabled, newValue);
                                                return;
                                              }

                                              // === GERENCIAR SELECIONADO ===
                                              if (isGerenciar) {
                                                const match = ability.nome.match(/_(\w+)$/);
                                                const suffix = match ? safeLower(match[1]) : null;

                                                if (suffix && newChecked[suffix]) {
                                                  if (checked) {
                                                    newChecked[suffix] = Object.fromEntries(
                                                        Object.entries(newChecked[suffix]).map(([id]) => [id, true])
                                                    );

                                                    const toDisable = grouped[suffix]
                                                        .filter((a) => a.id !== ability.id)
                                                        .map((a) => a.id);
                                                    newDisabled.push(...toDisable);

                                                    const groupIds = grouped[suffix].map((a) => a.id);
                                                    newValue = newValue.filter((id) => !groupIds.includes(id));
                                                    newValue.push(ability.id);
                                                  } else {
                                                    newChecked[suffix] = Object.fromEntries(
                                                        Object.entries(newChecked[suffix]).map(([id]) => [id, false])
                                                    );

                                                    const remaining = newDisabled.filter(
                                                        (id) => !grouped[suffix].some((a) => a.id === id)
                                                    );
                                                    newDisabled.splice(0, newDisabled.length, ...remaining);
                                                    newValue = newValue.filter((id) => id !== ability.id);
                                                  }

                                                  updateAbilities(newChecked, newDisabled, newValue);
                                                  return;
                                                }
                                              }

                                              // === NORMAL ===
                                              newChecked[group][ability.id] = checked;
                                              if (checked) {
                                                if (!newValue.includes(ability.id)) newValue.push(ability.id);
                                              } else {
                                                newValue = newValue.filter((id) => id !== ability.id);
                                              }

                                              // Auto-selecionar o “gerenciar” se tudo do grupo estiver marcado
                                              const gerenciarAbility = grouped[group].find(a =>
                                                  a.titulo.toLowerCase().startsWith('gerenciar')
                                              );

                                              if (gerenciarAbility) {
                                                const allChecked = grouped[group]
                                                    .filter(a => a.id !== gerenciarAbility.id)
                                                    .every(a => newChecked[group][a.id]);

                                                if (allChecked) {
                                                  // Marca automaticamente o “gerenciar” e bloqueia o resto
                                                  newChecked[group] = Object.fromEntries(
                                                      Object.entries(newChecked[group]).map(([id]) => [id, true])
                                                  );
                                                  const toDisable = grouped[group]
                                                      .filter(a => a.id !== gerenciarAbility.id)
                                                      .map(a => a.id);
                                                  newDisabled.push(...toDisable);

                                                  // Substitui todas do grupo apenas pelo “gerenciar”
                                                  const groupIds = grouped[group].map(a => a.id);
                                                  newValue = newValue.filter(id => !groupIds.includes(id));
                                                  newValue.push(gerenciarAbility.id);
                                                } else {
                                                  // Se desmarcou uma = libera tudo e desmarca o gerenciar
                                                  const remaining = newDisabled.filter(
                                                      id => !grouped[group].some(a => a.id === id)
                                                  );
                                                  newDisabled.splice(0, newDisabled.length, ...remaining);

                                                  newChecked[group][gerenciarAbility.id] = false;
                                                  newValue = newValue.filter(id => id !== gerenciarAbility.id);
                                                }
                                              }

                                              updateAbilities(newChecked, newDisabled, newValue);
                                            }}
                                        />
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </CardContent>
                        </Card>
                      </Grid>
                  ))}
                </Grid>
            );
          }}
      />
  );
}
